import React, { useState } from "react";
import {
  Container, Typography, Paper, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Select, MenuItem,
  FormControl, Checkbox, FormControlLabel, FormLabel, Box
} from "@mui/material";
import MagicIcon from "@mui/icons-material/AutoFixHigh";
import PlusIcon from "@mui/icons-material/Add";
import DemoTable from "../../../components/table/demoTable";

const columns = [
  { id: 'field', label: 'Field Name', minWidth: 150, align: 'start' },
  { id: 'type', label: 'Type', minWidth: 100, align: 'start' },
  { id: 'required', label: 'Required', minWidth: 100, align: 'start' },
  { id: 'action', label: 'Action', minWidth: 100, align: 'center', actionType: { edit: true, delete: true } },
];

const SchemaGen = () => {
  const [schema, setSchema] = useState([]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [newField, setNewField] = useState({ field: "", type: "", required: false });
  const [openDialog, setOpenDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const saveField = () => {
    if (newField.field.trim()) {
      if (editIndex !== null) {
        const updatedSchema = [...schema];
        updatedSchema[editIndex] = newField;
        setSchema(updatedSchema);
      } else {
        setSchema([...schema, newField]);
      }
      setNewField({ field: "", type: "String", required: false });
      setOpenDialog(false);
      setEditIndex(null);
    }
  };

  const handleDelete = (index) => {
    setSchema(schema.filter((_, i) => i !== index));
  };

  const handleEdit = (index) => {
    setNewField(schema[index]);
    setEditIndex(index);
    setOpenDialog(true);
  };

  const transformSchemas = (schema) =>
    schema.map((item, index) => ({
      field: item.field,
      type: item.type,
      required: item.required ? "Yes" : "No",
      action: index,
    }));

  const modifiedRows = schema ? transformSchemas(schema) : [];

  const generateSchemaFromAI = () => {
    // AI logic here
  };

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>
        Invoice Data Model
      </Typography>

      {/* AI Schema Generator Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MagicIcon color="primary" /> AI-Powered Schema Generator
        </Typography>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <FormLabel>Describe Your Invoice Data Structure</FormLabel>
          <TextField
            fullWidth
            multiline
            rows={5}
            placeholder="Describe your invoice data structure..."
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            sx={{ "& .MuiInputBase-root": { minHeight: "150px" } }}
          />
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          onClick={generateSchemaFromAI}
          sx={{ mt: 2 }}
        >
          Generate Schema
        </Button>
      </Paper>

      {/* Define Invoice Schema Section */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Define Invoice Schema
        </Typography>
        <DemoTable
          columns={columns}
          rows={modifiedRows}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
        />

        {/* Input fields always visible */}
        <Box sx={{ display: "flex",flexDirection: "row", alignItems: "center", gap: 2, mt: 2 }}>
          <TextField
            placeholder="Field Name"
            value={newField.field}
            sx={{ flex: 1 }}
            onChange={(e) => setNewField({ ...newField, field: e.target.value })}
          />
          <FormControl fullWidth sx={{ flex: 1 }}>
            <Select
              value={newField.type}
              onChange={(e) => setNewField({ ...newField, type: e.target.value })}
              displayEmpty
              variant="outlined"
            >
              <MenuItem value="" disabled>Select Type</MenuItem>
              <MenuItem value="String">String</MenuItem>
              <MenuItem value="Number">Number</MenuItem>
              <MenuItem value="Date">Date</MenuItem>
              <MenuItem value="Array">Array</MenuItem>
              <MenuItem value="Boolean">Boolean</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={newField.required} onChange={(e) => setNewField({ ...newField, required: e.target.checked })} />}
            label="Required"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlusIcon />}
            onClick={saveField} 
          >
            Add
          </Button>
        </Box>
      </Paper>

      {/* Dialog for Edit Field */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Field</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <FormLabel>Field Name</FormLabel>
            <TextField
              placeholder="Field Name"
              fullWidth
              value={newField.field}
              onChange={(e) => setNewField({ ...newField, field: e.target.value })}
            />
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FormControl fullWidth>
              <FormLabel>Type</FormLabel>
              <Select
                value={newField.type || ""}
                onChange={(e) => setNewField({ ...newField, type: e.target.value })}
                displayEmpty
              >
                <MenuItem value="" disabled>Select a Type</MenuItem>
                <MenuItem value="String">String</MenuItem>
                <MenuItem value="Number">Number</MenuItem>
                <MenuItem value="Date">Date</MenuItem>
                <MenuItem value="Array">Array</MenuItem>
                <MenuItem value="Boolean">Boolean</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Required</FormLabel>
              <Checkbox
                checked={newField.required}
                onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={saveField} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SchemaGen;